import { readingPane, skipToMessage } from './MailView.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda */
import { observer } from 'mobx-react';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ListPane } from 'owa-mail-list-pane-view';
import { listViewStore, shouldSuppressServerMarkReadOnReplyOrForward } from 'owa-mail-list-store';
import {
    lazySetListViewDimension,
    isReadingPanePositionBottom,
    isReadingPanePositionRight,
    shouldShowListView,
    shouldShowReadingPane,
    getListViewContainerStyles,
    getReadingPaneContainerStyles,
} from 'owa-mail-layout';
import ReadingPaneContainer from 'owa-mail-reading-pane-container/lib/components/ReadingPaneContainer';
import { NotificationBarHost } from 'owa-notification-bar';
import { lazyTriggerResizeEvent } from 'owa-resize-event';
import { ResizeHandle, ResizeHandleDirection } from 'owa-resize-handle';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { getStore as getTabStore, TabType } from 'owa-tab-store';
import { TabViewState } from 'owa-tab-store';
import { TabBar } from 'owa-tab-view';
import * as React from 'react';
import getMailListHeader from 'owa-mail-list-pane-view/lib/components/getMailListHeader';
import { globalStyles } from 'owa-global-styles';
import getStyleSelectorAsPerUserSettings from 'owa-mail-list-view/lib/utils/getStyleSelectorAsPerUserSettings';
import { addSkipLinkRegion } from 'owa-skip-link/lib/utils/addSkipLinkRegion';
import { NotesPane } from 'owa-notes';
import folderStore from 'owa-folders';
import { lazyResetFocus } from 'owa-mail-focus-manager';
import { isInboxPlusEnabled } from 'owa-inbox-plus-util/lib/util/inboxPlusFlightUtil';
import { MailHotkeysMap } from 'owa-mail-hotkeys';
import isNonInboxTabEnabled from 'owa-mail-inbox-plus-store/lib/selectors/isNonInboxTabEnabled';

const classNames = require('classnames');
const styles = require('./MailModule.scss');

export interface Props {
    activeTab: TabViewState;
    isDumpsterOrDumpsterSearchTable: boolean;
}

@observer
export default class MailView extends React.Component<Props, {}> {
    render() {
        const { activeTab, isDumpsterOrDumpsterSearchTable } = this.props;
        const tableViewId = listViewStore.selectedTableViewId;
        const tableView = listViewStore.tableViews.get(tableViewId);
        const styleSelectorAsPerUserSettings = getStyleSelectorAsPerUserSettings(tableViewId);

        const isRPPositionRight = isReadingPanePositionRight();
        const isRPPositionBottom = isReadingPanePositionBottom();

        let showReadingPane = shouldShowReadingPane();
        let showListPane = shouldShowListView();

        // Adjust list view & reading pane state for tab view
        let showTabBar = false;

        if (activeTab && !isDumpsterOrDumpsterSearchTable) {
            // Show tab bar when there is a secondary visible tab
            getTabStore().tabs.forEach(tab => {
                showTabBar = showTabBar || (tab.isShown && tab != activeTab);
            });

            if (isRPPositionBottom) {
                // When reading pane is on bottom and current tab is full compose, we need to hide list view
                // to give compose enough space.
                // Here we only check TabType.Compose. There are still 2 cases that compose can be show but
                // we don't check them here, because:
                // 1. current tab is Primary tab and we have primary compose. This happens when user is open
                // draft items folder. In that case we still show list view otherwise user can never see list
                // view of draft folder.
                // 2. inline compose. This should never happen when reading pane is on bottom.
                showListPane = showListPane && activeTab.type != TabType.Compose;
            } else if (!isRPPositionRight) {
                // When reading pane is off, need to adjust reading pane/list view according to current tab type
                // When active tab is primary tab, show reading pane/list view according to the setting in mailStore
                // Otherwise, always show reading pane since reading pane is the container for all other tabs
                showReadingPane = activeTab.type != TabType.Primary || shouldShowReadingPane();
                showListPane = activeTab.type == TabType.Primary && shouldShowListView();
            }
        }

        const showTabBarInListView =
            showTabBar && showListPane && !showReadingPane && !isDumpsterOrDumpsterSearchTable;
        const showTabBarInReadingPane =
            showTabBar && !showTabBarInListView && !isDumpsterOrDumpsterSearchTable;

        const mailListClasses = classNames(
            isRPPositionRight ? styles.mailList : styles.mailListSLV,
            !showListPane && styles.mailListHide,
            isRPPositionBottom && styles.mailListBottomBorder,
            globalStyles.addFocusOutline,
            getListViewContainerStyles()
        );

        const subCommandBarPaneClasses = classNames(
            isRPPositionBottom ? styles.subCommandBarPaneColumn : styles.subCommandBarPaneRow,
            styles.subCommandBarPaneCommon,
            styles.subCommandBarPaneMail
        );

        const showNotificationBar = !isRPPositionRight;

        const shouldShowHeader =
            isInboxPlusEnabled() &&
            isNonInboxTabEnabled() &&
            folderNameToId('inbox') === tableView.tableQuery.folderId &&
            isRPPositionRight &&
            !isRPPositionBottom;

        const isNotesFolder = tableView.tableQuery.folderId === folderNameToId('notes');

        const sourceFolder = folderStore.folderTable.get(tableView.tableQuery.folderId);
        if (
            !!sourceFolder &&
            folderIdToName(sourceFolder.FolderId.Id) == 'notes' &&
            isFeatureEnabled('notes-folder-view')
        ) {
            return <NotesPane header={getMailListHeader(tableViewId, styles.notesHeader)} />;
        }
        return (
            <div className={styles.rightPane}>
                {shouldShowHeader && getMailListHeader(tableViewId, styleSelectorAsPerUserSettings)}
                <div className={subCommandBarPaneClasses}>
                    <ListPane
                        className={mailListClasses}
                        tableViewId={tableViewId}
                        shouldShowHeader={shouldShowHeader}
                        tabBar={showTabBarInListView ? <TabBar /> : null}
                        styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                    />
                    {// Only show resize handle when Reading Pane and List Pane should be shown
                    showReadingPane && showListPane && (
                        <ResizeHandle
                            direction={
                                isRPPositionBottom
                                    ? ResizeHandleDirection.horizontal
                                    : ResizeHandleDirection.vertical
                            }
                            onResize={this.onRightPaneResize}
                        />
                    )}
                    {showReadingPane && (
                        <div
                            className={classNames(
                                styles.readingPane,
                                getReadingPaneContainerStyles()
                            )}>
                            {addSkipLinkRegion(
                                loc(skipToMessage),
                                'complementary',
                                <ReadingPaneContainer
                                    isDumpsterOrDumpsterSearchTable={
                                        isDumpsterOrDumpsterSearchTable
                                    }
                                    suppressServerMarkReadOnReplyOrForward={shouldSuppressServerMarkReadOnReplyOrForward(
                                        tableView
                                    )}
                                    isNotesFolder={isNotesFolder}
                                />,
                                loc(readingPane) //regionName
                            )}
                            {showTabBarInReadingPane && <TabBar />}
                        </div>
                    )}
                    {showNotificationBar && (
                        <NotificationBarHost
                            hostId={'MailModuleNotificationBarHost'}
                            onNotificationBlur={lazyResetFocus.importAndExecute}
                        />
                    )}
                </div>
                <MailHotkeysMap />
            </div>
        );
    }

    private onRightPaneResize(listViewDimension: number, readingPaneDimension: number) {
        const isWidth = isReadingPanePositionRight();
        lazySetListViewDimension.importAndExecute(listViewDimension, isWidth);
        lazyTriggerResizeEvent.importAndExecute();
    }
}
